import type { FC } from 'react'
import type { Theme } from '@mui/material'
import { useSortable } from '@dnd-kit/sortable'
import { useDroppable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { HolderOutlined, PlusOutlined, LockOutlined } from '@ant-design/icons'
import { Paper, Box, Typography, Chip, IconButton, Tooltip } from '@mui/material'
import { RewardCard } from './RewardCard'

interface CategoryCardProps {
  category: any
  isDraggable: boolean
  selectedRewards: Set<string>
  onToggleRewardSelect: (id: string) => void
  onRewardEdit: (id: string) => void
  onRewardDelete: (id: string) => void
  onAddReward: (categoryId: string) => void
  theme: Theme
}

export const CategoryCard: FC<CategoryCardProps> = ({
  category,
  isDraggable,
  selectedRewards,
  onToggleRewardSelect,
  onRewardEdit,
  onRewardDelete,
  onAddReward,
  theme,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef: setCategoryNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: category.id,
    disabled: !isDraggable,
  })

  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: `category-droppable-${category.categoryId}`,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <Paper
      ref={setCategoryNodeRef}
      style={style}
      {...attributes}
      elevation={2}
      sx={{
        mb: 1.5,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Category Header - Draggable */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          borderBottom: `2px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          cursor: isDraggable ? 'grab' : 'default',
          backgroundColor: theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[100],
        }}
        {...(isDraggable ? listeners : {})}
      >
        {isDraggable ? (
          <HolderOutlined style={{ fontSize: 16, opacity: 0.5 }} />
        ) : (
          <Tooltip title="Primary category cannot be reordered">
            <LockOutlined style={{ fontSize: 16, opacity: 0.5 }} />
          </Tooltip>
        )}

        <Typography variant="subtitle1" fontWeight={600} sx={{ flexGrow: 1, fontSize: '0.95rem' }}>
          {category.categoryName}
        </Typography>

        <Chip label={`${category.rewardCount}`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />

        <IconButton size="small" onClick={() => onAddReward(category.categoryId)} sx={{ padding: 0.5 }}>
          <PlusOutlined style={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {/* Rewards List - Droppable Zone */}
      <Box
        ref={setDroppableRef}
        sx={{
          p: 1,
          minHeight: category.subRows.length === 0 ? 80 : 'auto',
          backgroundColor: isOver ? theme.palette.action.hover : 'transparent',
          border: category.subRows.length === 0 ? `2px dashed ${theme.palette.divider}` : 'none',
          borderRadius: category.subRows.length === 0 ? 1 : 0,
        }}
      >
        {category.subRows.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', py: 2 }}>
            <Typography color="textSecondary" variant="caption">
              Drag rewards here or add new ones
            </Typography>
          </Box>
        ) : (
          <SortableContext items={category.subRows.map((r: any) => r.id!)} strategy={verticalListSortingStrategy}>
            {category.subRows.map((reward: any) => (
              <RewardCard
                key={reward.id}
                reward={reward}
                isSelected={selectedRewards.has(reward.id!)}
                onToggleSelect={onToggleRewardSelect}
                onEdit={onRewardEdit}
                onDelete={onRewardDelete}
              />
            ))}
          </SortableContext>
        )}
      </Box>
    </Paper>
  )
}
